import { 
  getFirestore, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  collection,
  writeBatch,
  serverTimestamp 
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { initializeApp, getApp } from 'firebase/app';
import { firebaseConfig } from '../../firebase.config';

// Initialize Firebase if not already initialized
let app;
try {
  app = getApp();
} catch (error) {
  app = initializeApp(firebaseConfig);
}

const db = getFirestore(app);
const auth = getAuth(app);

/**
 * Firestore Data Sync Service
 * Handles syncing flashcard data between localStorage and Firestore
 */
export const firestoreSyncService = {
  /**
   * Save user's classes to Firestore
   */
  async saveClasses(userId, classes) {
    if (!userId || !classes) return;
    
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        classes: classes,
        lastSyncedAt: serverTimestamp(),
      });
      console.log('✅ Classes synced to Firestore');
    } catch (error) {
      console.warn('⚠️ Could not save classes to Firestore:', error.message);
      // Don't throw - allow app to continue working with localStorage
    }
  },

  /**
   * Save user's flashcard sets to Firestore
   */
  async saveFlashcardSets(userId, flashcardSets) {
    if (!userId || !flashcardSets) return;
    
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        flashcardSets: flashcardSets,
        lastSyncedAt: serverTimestamp(),
      });
      console.log('✅ Flashcard sets synced to Firestore');
    } catch (error) {
      console.warn('⚠️ Could not save flashcard sets to Firestore:', error.message);
    }
  },

  /**
   * Save user's flashcards to Firestore
   */
  async saveFlashcards(userId, flashcards) {
    if (!userId || !flashcards) return;
    
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        flashcards: flashcards,
        lastSyncedAt: serverTimestamp(),
      });
      console.log('✅ Flashcards synced to Firestore');
    } catch (error) {
      console.warn('⚠️ Could not save flashcards to Firestore:', error.message);
    }
  },

  /**
   * Save user's card progress to Firestore
   */
  async saveCardProgress(userId, cardProgress) {
    if (!userId || !cardProgress) return;
    
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        cardProgress: cardProgress,
        lastSyncedAt: serverTimestamp(),
      });
      console.log('✅ Card progress synced to Firestore');
    } catch (error) {
      console.warn('⚠️ Could not save card progress to Firestore:', error.message);
    }
  },

  /**
   * Save user's studied sets to Firestore
   */
  async saveStudiedSets(userId, studiedSets) {
    if (!userId || !studiedSets) return;
    
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        studiedSets: studiedSets,
        lastSyncedAt: serverTimestamp(),
      });
      console.log('✅ Studied sets synced to Firestore');
    } catch (error) {
      console.warn('⚠️ Could not save studied sets to Firestore:', error.message);
    }
  },

  /**
   * Save all user data to Firestore in a batch
   * This is more efficient when saving multiple items at once
   */
  async saveAllData(userId, data) {
    if (!userId) return;
    
    try {
      const userDocRef = doc(db, 'users', userId);
      
      const dataToSave = {
        lastSyncedAt: serverTimestamp(),
      };

      if (data.classes !== undefined) dataToSave.classes = data.classes;
      if (data.flashcardSets !== undefined) dataToSave.flashcardSets = data.flashcardSets;
      if (data.flashcards !== undefined) dataToSave.flashcards = data.flashcards;
      if (data.cardProgress !== undefined) dataToSave.cardProgress = data.cardProgress;
      if (data.studiedSets !== undefined) dataToSave.studiedSets = data.studiedSets;

      await updateDoc(userDocRef, dataToSave);
      console.log('✅ All data synced to Firestore');
    } catch (error) {
      // If document doesn't exist, create it
      if (error.code === 'not-found') {
        try {
          const userDocRef = doc(db, 'users', userId);
          await setDoc(userDocRef, {
            uid: userId,
            classes: data.classes || [],
            flashcardSets: data.flashcardSets || [],
            flashcards: data.flashcards || [],
            cardProgress: data.cardProgress || {},
            studiedSets: data.studiedSets || [],
            lastSyncedAt: serverTimestamp(),
          }, { merge: true });
          console.log('✅ User document created and data synced');
        } catch (createError) {
          console.warn('⚠️ Could not create user document:', createError.message);
        }
      } else {
        console.warn('⚠️ Could not save all data to Firestore:', error.message);
      }
    }
  },

  /**
   * Load all user data from Firestore
   * Returns null if data doesn't exist or error occurs
   */
  async loadAllData(userId) {
    if (!userId) return null;
    
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        return {
          classes: data.classes || [],
          flashcardSets: data.flashcardSets || [],
          flashcards: data.flashcards || [],
          cardProgress: data.cardProgress || {},
          studiedSets: data.studiedSets || [],
          lastSyncedAt: data.lastSyncedAt,
        };
      }
      
      console.log('ℹ️ No Firestore data found for user');
      return null;
    } catch (error) {
      console.warn('⚠️ Could not load data from Firestore:', error.message);
      return null;
    }
  },

  /**
   * Migrate localStorage data to Firestore
   * This runs once when user logs in and has localStorage data but no Firestore data
   */
  async migrateLocalStorageToFirestore(userId, localData) {
    if (!userId || !localData) return false;
    
    try {
      console.log('🔄 Migrating localStorage data to Firestore...');
      
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      // Check if Firestore already has data
      const firestoreData = userDoc.exists() ? userDoc.data() : null;
      const hasFirestoreData = firestoreData && (
        (firestoreData.classes && firestoreData.classes.length > 0) ||
        (firestoreData.flashcardSets && firestoreData.flashcardSets.length > 0) ||
        (firestoreData.flashcards && firestoreData.flashcards.length > 0)
      );
      
      if (hasFirestoreData) {
        console.log('ℹ️ Firestore already has data, skipping migration');
        return false;
      }
      
      // Migrate data to Firestore
      await this.saveAllData(userId, localData);
      console.log('✅ Migration completed successfully');
      return true;
    } catch (error) {
      console.error('❌ Migration failed:', error.message);
      return false;
    }
  },

  /**
   * Check if user has data in Firestore
   */
  async hasFirestoreData(userId) {
    if (!userId) return false;
    
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) return false;
      
      const data = userDoc.data();
      return (
        (data.classes && data.classes.length > 0) ||
        (data.flashcardSets && data.flashcardSets.length > 0) ||
        (data.flashcards && data.flashcards.length > 0)
      );
    } catch (error) {
      console.warn('⚠️ Could not check Firestore data:', error.message);
      return false;
    }
  },

  /**
   * Debounced save - useful for frequent updates
   * Collects multiple updates and saves them in batches
   */
  _saveTimeouts: {},
  
  debouncedSave(userId, dataType, data, delay = 2000) {
    if (!userId) return;
    
    // Clear existing timeout
    if (this._saveTimeouts[dataType]) {
      clearTimeout(this._saveTimeouts[dataType]);
    }
    
    // Set new timeout
    this._saveTimeouts[dataType] = setTimeout(async () => {
      switch (dataType) {
        case 'classes':
          await this.saveClasses(userId, data);
          break;
        case 'flashcardSets':
          await this.saveFlashcardSets(userId, data);
          break;
        case 'flashcards':
          await this.saveFlashcards(userId, data);
          break;
        case 'cardProgress':
          await this.saveCardProgress(userId, data);
          break;
        case 'studiedSets':
          await this.saveStudiedSets(userId, data);
          break;
        case 'all':
          await this.saveAllData(userId, data);
          break;
      }
      delete this._saveTimeouts[dataType];
    }, delay);
  },
};

export default firestoreSyncService;

